import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { Key, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as scenarios from 'api/scenarios';
import { Alert, Button } from 'components';
import { Snackbar } from 'components/Snackbar';
import { CardMeta, CardMetaGrid, ManagementCard, Pill } from 'components/ManagementCard';
import { pageCardClasses } from 'components/AppLayout/styles';
import { routes } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import { ELineChannelStatus } from 'types';
import { apiIssueCode, issueCodeMessage, issueMessage } from 'utils/scenarioIssues';

import { stateLabels, stateTones } from '../labels';
import { resolveTargets, usableAccount } from '../targets';

const number = new Intl.NumberFormat('ja-JP').format;
const date = new Intl.DateTimeFormat('ja-JP', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Tokyo',
}).format;
// prettier-ignore
const statusLabels = { active: '進行中', completed: '完了', cancelled: '取消', failed: '失敗' } as const;
const assetLabels = { richMessage: 'リッチメッセージ', cardTypeMessage: 'カードタイプ' } as const;
// prettier-ignore
const stepHeadings = ['ステップ', '対象', '到達', '到達率', '送信成功', '失敗', '処理中', '送信前取消', '試行後取消', '送信成功率'];
const urlClickHeadings = ['ステップ', 'URL', 'クリック数', 'ユニーク', 'クリック率'];
const failureHeadings = ['友だち', '公開版 / ステップ', '再開期限の基準', '失敗理由', '再開期限', '操作'];
type Row = [Key, ReactNode[]];
type DetailPage = scenarios.ScenarioDetailResponse | scenarios.ScenarioRevisionPageResponse;

function rate(value: number | null, top: number, bottom: number) {
  return value === null ? '—' : `${(value * 100).toFixed(1)}% (${number(top)}/${number(bottom)})`;
}

function remaining(value: string | null, now: number) {
  if (!value) return '期限情報なし';
  const minutes = Math.ceil((Date.parse(value) - now) / 60_000);
  if (minutes <= 0) return '再開期限切れ';
  if (minutes < 60) return `残り${minutes}分`;
  return `残り${Math.floor(minutes / 60)}時間${minutes % 60 ? `${minutes % 60}分` : ''}`;
}

function resumeIssue(error: unknown) {
  const source = error as { status?: number; response?: { status?: number; data?: { code?: unknown } } };
  return { status: source.response?.status ?? source.status, code: typeof source.response?.data?.code === 'string' ? source.response.data.code : undefined };
}

// codeが無い応答でもstatusだけで案内できるよう、再開固有の409を挟んでから共有マップへ委ねる。
function resumeMessage(error: unknown) {
  const { status, code } = resumeIssue(error);
  if (code === 'RESUME_EXPIRED' || status === 410) return issueCodeMessage('RESUME_EXPIRED');
  if (code === 'DISPATCH_BUSY' || status === 503) return issueCodeMessage('DISPATCH_BUSY');
  if (status === 409) return '配信状態が変わったため再開できません。最新の状態を表示しています。';
  return issueMessage(error, '配信を再開できませんでした。');
}

// 200でも再送に失敗・結果不明のまま返ることがあるため、statusごとに結果を明示する。
// undefinedは成功（sent）で、通知はSnackbarに任せる
function resumeOutcomeNotice(result: scenarios.ScenarioResumeResponse): string | undefined {
  if (result.status === 'sent') return undefined;
  if (result.status === 'failed') {
    return result.retrying
      ? '送信に失敗しました。自動で再送を待機しています。'
      : '送信に失敗しました。失敗一覧から再度お試しください。';
  }
  if (result.status === 'unknown') return '送信結果を確認できませんでした。届いている可能性があるため、受信を確認してから再実行してください。';
  if (result.status === 'cancelled') return '配信が取り消されていたため送信しませんでした。';
  return '配信状態が変わったため送信しませんでした。最新の状態を表示しています。';
}

function canResume(row: scenarios.ScenarioFailure, now: number) {
  const expiresAt = row.retryExpiresAt ? Date.parse(row.retryExpiresAt) : NaN;
  return row.resumable && Number.isFinite(expiresAt) && expiresAt > now;
}

function failureReason(row: scenarios.ScenarioFailure) {
  if (row.failureType === 'RESULT_UNKNOWN_AFTER_EXPIRY') {
    return '送信結果不明・再開期限切れ（届いている可能性があります）';
  }
  if (row.failureType === 'RESULT_UNKNOWN_AFTER_ATTEMPT_LIMIT') {
    return '送信結果不明・自動試行上限（届いている可能性があります）';
  }
  return row.failureReason || row.failureType || (row.httpStatus ? `HTTP ${row.httpStatus}` : '詳細なし');
}

function retryBasis(row: scenarios.ScenarioFailure) {
  if (row.firstAttemptAt) return date(new Date(row.firstAttemptAt));
  if (row.retryExpiresAt) return date(new Date(Date.parse(row.retryExpiresAt) - 24 * 60 * 60 * 1000));
  return '—';
}

function stepRow(step: scenarios.ScenarioStatsStep): Row {
  return [
    step.stepId,
    [
      step.stepOrder,
      number(step.targetCount),
      number(step.reachedCount),
      rate(step.reachRate, step.reachedCount, step.targetCount),
      number(step.sent),
      number(step.failed),
      number(step.processing),
      number(step.cancelledBeforeAttempt),
      number(step.cancelledAfterAttempt),
      rate(step.successRate, step.sent, step.sent + step.failed),
    ],
  ];
}

// クリック数は同じリンクへの有効なアクセス回数、ユニークは押した配信（Run）の実数。
// 受信者refを付ける前に送ったリンクのクリックはRunを特定できないため、ユニークには入らない
function urlClickRow(click: scenarios.ScenarioStatsUrlClick, stepOrderById: Map<string, number>, index: number): Row {
  return [
    `${click.stepId}:${index}`,
    [
      stepOrderById.get(click.stepId) ?? '—',
      click.displayUrl,
      click.clickCount === null ? '—（未計測）' : number(click.clickCount),
      click.uniqueClickCount === null ? '—' : number(click.uniqueClickCount),
      click.clickRate === null ? '—' : `${(click.clickRate * 100).toFixed(1)}%`,
    ],
  ];
}

/** numericFrom以降の列は数値なので右寄せ・等幅数字にする。 */
function Table({ headings, rows, numericFrom }: { headings: string[]; rows: Row[]; numericFrom?: number }) {
  const numeric = (index: number) => numericFrom !== undefined && index >= numericFrom;
  // prettier-ignore
  return (
    <div className="table-scroll overflow-x-auto rounded-2xl border border-border/70 bg-white">
      <table className="min-w-[1040px] w-full text-left text-xs text-ink sm:text-[13px]">
        <thead className="bg-surface/70 text-[10px] font-semibold text-muted sm:text-[11px]">
          <tr>{headings.map((item, index) => <th key={item} className={`whitespace-nowrap px-4 py-3 ${numeric(index) ? 'text-right' : ''}`}>{item}</th>)}</tr>
        </thead>
        <tbody>{rows.map(([key, cells]) => (
            <tr key={key} className="border-t border-border/60 align-top">
              {cells.map((cell, index) => <td key={headings[index]} className={numeric(index) ? 'whitespace-nowrap px-4 py-4 text-right tabular-nums' : 'max-w-xs whitespace-normal px-4 py-4'}>{cell}</td>)}
            </tr>
          ))}</tbody>
      </table>
    </div>
  );
}

/**
 * スマホ幅では表が横スクロールに逃げ、最終列の操作へ届かなくなる。
 * 他の一覧画面と同じくカードへ組み替える。
 */
function CardList({ headings, rows, actionIndex, cardTitle }: { headings: string[]; rows: Row[]; actionIndex?: number; cardTitle?: (cells: ReactNode[]) => ReactNode }) {
  // prettier-ignore
  return (
    <div className="grid gap-3">
      {rows.map(([key, cells]) => (
        <ManagementCard
          key={key}
          title={cardTitle ? cardTitle(cells) : cells[0]}
          actions={actionIndex === undefined ? undefined : cells[actionIndex]}
        >
          <CardMetaGrid>
            {cells.map((cell, index) => index === 0 || index === actionIndex ? null : <CardMeta key={headings[index]} label={headings[index]} value={cell} />)}
          </CardMetaGrid>
        </ManagementCard>
      ))}
    </div>
  );
}

/** 幅の広い表はPCで表、スマホでカードにする。 */
function RowList({ headings, rows, numericFrom, actionIndex, cardTitle }: { headings: string[]; rows: Row[]; numericFrom?: number; actionIndex?: number; cardTitle?: (cells: ReactNode[]) => ReactNode }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table headings={headings} rows={rows} numericFrom={numericFrom} />
      </div>
      <div className="lg:hidden">
        <CardList headings={headings} rows={rows} actionIndex={actionIndex} cardTitle={cardTitle} />
      </div>
    </>
  );
}

function Message({ children }: { children: ReactNode }) {
  return <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted">{children}</p>;
}

export default function ScenarioStats() {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const client = useQueryClient();
  const scenarioId = Number(useParams().scenarioId);
  const validId = Number.isInteger(scenarioId) && scenarioId > 0;
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'failures' ? 'failures' : 'summary';
  const [revisionId, setRevisionId] = useState<number | null>(null);
  // 既定を「全公開版」にするとステップ別表が空のまま開く。最頻の用途は現行版の結果なので、
  // 初回読み込み時だけ現在使用中の版を選ぶ（以後は利用者の選択を尊重する）
  const revisionInitialized = useRef(false);
  const [now, setNow] = useState(() => Date.now());
  const [resumeNotice, setResumeNotice] = useState<string>();
  const [resumeSuccess, setResumeSuccess] = useState<string>();
  const [cursorNotice, setCursorNotice] = useState<string>();

  const detail = useInfiniteQuery<DetailPage, Error, InfiniteData<DetailPage>, (string | number)[], string | null>({
    queryKey: ['scenario-detail', scenarioId, 'revisions'],
    enabled: validId,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => (pageParam ? scenarios.getScenario(scenarioId, { revisionCursor: pageParam }) : scenarios.getScenario(scenarioId)),
    getNextPageParam: (page) => page.nextRevisionCursor ?? undefined,
    retry: false,
  });
  const scenario = detail.data?.pages[0]?.detail ?? null;
  useEffect(() => {
    if (revisionInitialized.current || !scenario) return;
    revisionInitialized.current = true;
    if (scenario.currentRevisionId) setRevisionId(scenario.currentRevisionId);
  }, [scenario]);
  const targets = scenario ? resolveTargets(scenario) : { allAccounts: true, accounts: [] };
  const singleTarget = !targets.allAccounts && targets.accounts.length === 1 ? targets.accounts[0] : undefined;
  // 全アカウント対象と複数対象は共通のアカウント状態を持たないため、再開可否はRunごとにserverが判定する。
  // 対象が複数あるときは1つでも使えれば再開を許す。
  const accountCanResume = targets.allAccounts || !targets.accounts.length || targets.accounts.some(({ status }) => usableAccount(status));
  const accountBlockReason = accountCanResume
    ? undefined
    : singleTarget
      ? singleTarget.status === ELineChannelStatus.PAUSED
        ? 'LINE公式アカウントが停止中のため、配信を再開できません。'
        : singleTarget.status === ELineChannelStatus.UNLINKED
          ? 'LINE公式アカウントが連携解除済みのため、配信を再開できません。'
          : 'LINE公式アカウントの状態を確認できないため、配信を再開できません。'
      : '対象のLINE公式アカウントがすべて停止中または連携解除済みのため、配信を再開できません。';
  const revisions = detail.data?.pages.flatMap((page) => page.revisionSummaries) ?? [];
  const summary = useQuery({
    queryKey: ['scenario-stats', scenarioId, 'summary', revisionId ?? 'all'],
    enabled: Boolean(scenario),
    queryFn: () => scenarios.getScenarioStats(scenarioId, revisionId ? { view: 'summary', revisionId } : { view: 'summary' }),
    retry: false,
  });
  const selectedRevision = useQuery({
    queryKey: ['scenario-detail', scenarioId, 'revision', revisionId],
    enabled: Boolean(scenario && revisionId),
    queryFn: () => scenarios.getScenario(scenarioId, { revisionId: revisionId! }),
    retry: false,
  });
  const failures = useInfiniteQuery({
    queryKey: ['scenario-stats', scenarioId, 'failures', revisionId ?? 'all'],
    enabled: Boolean(scenario) && view === 'failures',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      scenarios.getScenarioStats(scenarioId, {
        view: 'failures',
        ...(revisionId ? { revisionId } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
  });
  const refreshResumeState = () => Promise.all([
    client.invalidateQueries({ queryKey: ['scenario-stats', scenarioId] }),
    client.invalidateQueries({ queryKey: ['scenario-detail', scenarioId] }),
    client.invalidateQueries({ queryKey: ['scenarios'] }),
  ]);
  const resume = useMutation({
    mutationFn: (id: number) => scenarios.resumeScenarioEnrollment(scenarioId, id),
    onMutate: () => { setResumeNotice(undefined); setResumeSuccess(undefined); },
    // 200でもfailed／unknown等で返ることがあるため、statusを見てから結果を伝える
    onSuccess: (result) => {
      const notice = resumeOutcomeNotice(result);
      setResumeNotice(notice);
      if (!notice) setResumeSuccess('再送信しました。');
      return refreshResumeState();
    },
    onError: (error) => {
      const { status } = resumeIssue(error);
      setResumeNotice(resumeMessage(error));
      if (status === 409 || status === 410) return refreshResumeState();
    },
  });

  const invalidCursor = [detail.error, failures.error].some((error) => apiIssueCode(error) === 'INVALID_CURSOR');
  useEffect(() => {
    if (!invalidCursor) return;
    setCursorNotice('表示位置が古くなったため、先頭から読み込み直しました。');
    void Promise.all([
      client.resetQueries({ queryKey: ['scenario-detail', scenarioId, 'revisions'] }),
      client.resetQueries({ queryKey: ['scenario-stats', scenarioId, 'failures'] }),
    ]);
  }, [invalidCursor, client, scenarioId]);
  const failureItems = failures.data?.pages.flatMap((page) => page.items) ?? [];
  const nextExpiryAt = failureItems.reduce<number | null>((next, row) => {
    const expiresAt = row.retryExpiresAt ? Date.parse(row.retryExpiresAt) : NaN;
    return expiresAt > now && (next === null || expiresAt < next) ? expiresAt : next;
  }, null);
  useEffect(() => {
    if (nextExpiryAt === null) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(1, Math.min(60_000, nextExpiryAt - Date.now())));
    return () => window.clearTimeout(timer);
  }, [nextExpiryAt, now]);
  // URL別表はstep IDしか持たないため、ステップ別表と同じ並び番号へ対応づける
  const stepOrderById = new Map((summary.data?.steps ?? []).map((step) => [step.stepId, step.stepOrder]));
  const failureRows = failureItems.map((row): Row => {
    const resumable = canResume(row, now);
    return [
      row.deliveryId,
      [
        row.friendName || `#${row.friendId}`,
        `第${row.publishNumber}版 / ステップ${row.stepOrder}`,
        retryBasis(row),
        <span className="text-danger">{failureReason(row)}</span>,
        <span title={row.retryExpiresAt ? date(new Date(row.retryExpiresAt)) : undefined}>
          <b className={row.retrying ? 'text-amber-700' : resumable ? 'text-emerald-700' : 'text-muted'}>{row.retrying ? '自動再試行中' : resumable ? '再開可能' : '再開不可'}</b> {remaining(row.retryExpiresAt, now)}
        </span>,
        <Button size="sm" title={accountBlockReason} disabled={scenario?.state !== 'active' || !accountCanResume || row.retrying || !resumable || resume.isPending} loading={resume.isPending && resume.variables === row.enrollmentId} onClick={() => resume.mutate(row.enrollmentId)}>
          配信を再開
        </Button>,
      ],
    ];
  });
  const selectedDetail = selectedRevision.data?.detail ?? null;
  const queryError = detail.error || summary.error || selectedRevision.error || failures.error;
  const error = !validId ? 'シナリオIDが正しくありません。' : queryError ? issueMessage(queryError, '統計を取得できませんでした。') : undefined;
  const changeView = (next: 'summary' | 'failures') => {
    const updated = new URLSearchParams(params);
    next === 'failures' ? updated.set('view', next) : updated.delete('view');
    setParams(updated);
  };
  const back = () =>
    navigate({
      pathname: orgPathFor(routes.SCENARIOS),
      ...(targets.allAccounts || !targets.accounts.length ? {} : { search: `?lineChannelId=${targets.accounts[0].id}` }),
    });

  // prettier-ignore
  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-muted">ステップ配信 / 統計</p>
            {/* 集計範囲は下の「全期間の登録状況」で説明するので、見出しでは状態だけを一覧・編集画面と同じPillで示す */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-display text-ink">{scenario?.name ?? 'シナリオ統計'}</h1>
              {scenario && <Pill tone={stateTones[scenario.state]}>{stateLabels[scenario.state]}</Pill>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={back}>一覧へ戻る</Button>
        </header>
        <div className="mt-6 grid gap-5">
          {error && <Alert>{error}</Alert>}
          {resumeNotice && <Alert>{resumeNotice}</Alert>}
          {cursorNotice && <Alert>{cursorNotice}</Alert>}
          {detail.isPending ? <Message>読み込み中...</Message> : scenario ? <>
            <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-border/70 bg-white p-4">
              <label className="grid min-w-[260px] flex-1 gap-2 text-xs font-semibold">公開版
                <select className="h-11 rounded-2xl border border-border/80 bg-white px-3 text-sm" value={revisionId ?? ''} onChange={(event) => setRevisionId(event.target.value ? Number(event.target.value) : null)}>
                  <option value="">全公開版</option>
                  {revisions.map((revision) => <option key={revision.revisionId} value={revision.revisionId}>第{revision.publishNumber}版（{date(new Date(revision.publishedAt))}）{revision.revisionId === scenario.currentRevisionId ? ' ・現在使用中' : ''}</option>)}
                </select>
              </label>
              {detail.hasNextPage && <Button size="sm" variant="outline" loading={detail.isFetchingNextPage} onClick={() => detail.fetchNextPage()}>公開版をさらに表示</Button>}
            </div>
            {revisionId !== null && <section className="grid gap-3 rounded-2xl border border-border/70 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2"><h2 className="text-sm font-semibold">公開版の内容（読み取り専用）</h2>{revisionId === scenario.currentRevisionId && <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">現在使用中</span>}</div>
              {selectedRevision.isPending ? <p className="text-xs text-muted">読み込み中...</p> : selectedDetail && <>
                <p className="text-xs text-muted">ステップ {selectedDetail.definition.steps.length}件 / 参照素材 {selectedDetail.assetReferences.length}件</p>
                {selectedDetail.assetReferences.length > 0 && <ul className="grid gap-2 sm:grid-cols-2">{selectedDetail.assetReferences.map((asset) => <li key={`${asset.messageType}-${asset.assetId}`} className={`rounded-xl border p-3 text-xs ${asset.status === 'deleted' ? 'border-red-200 bg-red-50 text-red-700' : 'border-border/70'}`}><span className="text-muted">{assetLabels[asset.messageType]}: </span>{asset.status === 'available' ? `${asset.name} (#${asset.assetId})` : `削除済み素材 #${asset.assetId}`}</li>)}</ul>}
              </>}
            </section>}
            <section className="grid gap-3">
              <div>
                <h2 className="text-sm font-semibold">全期間の登録状況</h2>
                {/* 同じ友だちでもきっかけが違えば別の登録になる（ユニーク制約が sourceType+sourceKey を含む）ため、人数とは言い切らない */}
                <p className="mt-1 text-xs text-muted">このシナリオに登録された件数と、その後の進み具合です。同じ友だちでも、別のきっかけで登録されると別の1件として数えます。</p>
              </div>
              {summary.isPending ? <Message>集計中...</Message> : summary.data && <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {[['登録の合計', summary.data.runCount, false] as const, ...Object.entries(statusLabels).map(([key, label]) => [label, summary.data.statusCounts[key as keyof typeof statusLabels], key === 'failed'] as const)].map(([label, count, isFailure]) => (
                  <div key={label} className={`grid content-start gap-1 rounded-2xl border p-4 ${isFailure && count > 0 ? 'border-danger-border bg-danger-soft' : 'border-border/70 bg-white'}`}>
                    <dt className="text-xs text-muted">{label}</dt>
                    <dd className={`break-all text-2xl font-semibold tabular-nums sm:text-3xl ${isFailure && count > 0 ? 'text-danger' : 'text-ink'}`}>{number(count)}</dd>
                  </div>
                ))}
              </dl>}
            </section>
            <div className="flex gap-2 border-b border-border/70 pb-3">
              {/* 見た目の差だけだと支援技術へどちらを表示中か伝わらない */}
              {([['summary', 'ステップ別統計'], ['failures', '失敗一覧']] as const).map(([key, label]) => <Button key={key} size="sm" aria-pressed={view === key} variant={view === key ? 'primary' : 'outline'} onClick={() => changeView(key)}>{label}</Button>)}
            </div>
            {view === 'summary'
              ? revisionId === null ? <Message>{revisions.length ? 'ステップ別の結果を見るには公開版を選択してください。' : '公開版はまだありません。'}</Message>
                : summary.isPending ? <Message>読み込み中...</Message>
                  : summary.data?.steps?.length ? <>
                      <p className="text-xs text-muted">対象＝このステップを受け取る予定だった件数、到達＝実際にこのステップまで進んだ件数です。</p>
                      <RowList headings={stepHeadings} rows={summary.data.steps.map(stepRow)} numericFrom={1} cardTitle={(cells) => `ステップ${cells[0]}`} />
                      {summary.data.urlClicks?.length ? <>
                        <h3 className="text-sm font-semibold text-ink">URL別クリック数</h3>
                        <p className="text-xs text-muted">同じ人が複数回押した場合もそれぞれ1回として数えます。</p>
                        <RowList headings={urlClickHeadings} rows={summary.data.urlClicks.map((click, index) => urlClickRow(click, stepOrderById, index))} numericFrom={2} cardTitle={(cells) => `ステップ${cells[0]}`} />
                      </> : null}
                    </>
                    : <Message>表示できるステップはありません。</Message>
              : failures.isPending ? <Message>読み込み中...</Message>
                : failureRows.length ? <RowList headings={failureHeadings} rows={failureRows} actionIndex={failureHeadings.length - 1} />
                  : <Message>配信失敗はありません。</Message>}
            {view === 'failures' && scenario.state !== 'active' && <p className="text-xs text-amber-700">シナリオを再開すると、期限内の失敗した配信を再開できます。</p>}
            {view === 'failures' && accountBlockReason && <p className="text-xs text-amber-700">{accountBlockReason}</p>}
            {view === 'failures' && failures.hasNextPage && <Button variant="outline" loading={failures.isFetchingNextPage} onClick={() => failures.fetchNextPage()}>さらに表示</Button>}
          </> : !error ? <Message>シナリオが見つかりません。</Message> : null}
        </div>
      </section>
      <Snackbar open={Boolean(resumeSuccess)} message={resumeSuccess} variant="success" onClose={() => setResumeSuccess(undefined)} autoHideDuration={3000} />
    </>
  );
}
